import { KeyboardSensor, MouseSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useDispatch } from "react-redux";
import { useState } from "react";
import KanbanCard from "../../../../../components/Kanban/Card";
import Kanban from "../../../../../components/Kanban/Kanban";
import { getAllPermissions, deleteRole, getAllRolesAndPermissions, removeRolePermission, addRolePermission } from "../../../store/userManagement/userManagementThunks";

export default function ManageRoles({ selectedEmployeeAvatar, elementsPerPage = 100 }) {
    const [searchTerm, setSearchTerm] = useState("");

    const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor);
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);

    const dispatch = useDispatch();

    const gestionRolesConfig = {
        getData: async (config, selectedEmployeeAvatar, setters, localPermissions, permissions) => {
            const { setLoading, setColumns, setCards, setLocalCards } = setters;
            setLoading(true);
            setColumns([]);
            setCards([]);
            setLocalCards([]);

            let localRoles = [];
            let permissions2 = [];
            const tenantId = selectedEmployeeAvatar?.tenant_id || 1;

            // Single API call instead of 1 + N + M calls
            const rolesWithPermissions = await dispatch(getAllRolesAndPermissions()).unwrap();

            if (!Array.isArray(rolesWithPermissions)) {
                return;
            }

            // Transform roles data
            rolesWithPermissions.forEach((role) => {
                localRoles.push({
                    pos: role.id,
                    id: role.name,
                    title: role.title,
                    description: role.description,
                    role_id: role.id,
                });
            });

            // Transform permissions data with proper indexing
            rolesWithPermissions.forEach(role => {
                role.permissionsLists?.forEach((permission) => { // Adjust alias based on your association
                    permissions2.push({
                        id: permissions2.length,
                        permission_id: permission.id,
                        name: permission.name,
                        role: role.id, // Using role.id as the role identifier
                    });
                });
            });

            return [localRoles, permissions2];
        },

        onCardDragEnd: async (config, permissions, permissionId, roles, newRole) => {
            const newRoleId = roles.find((r) => r.id === newRole).pos;
            const newPerm = permissions.find((u) => u.id === permissionId);
            await dispatch(
                addRolePermission({
                    params: [{ role_id: newRoleId, permission_id: newPerm.permission_id }],
                })
            );
            let localPermissions = [];
            if (newPerm) {

                localPermissions.push({
                    id: localPermissions.length !== 0 ? localPermissions.at(-1).id + 1 : 0,
                    name: newPerm.name,
                    role: newRole,
                });
            }
            return permissions.concat(localPermissions);
        },

        filterColumns: (roles, searchColumn) => {
            return roles ? roles.filter((role) => role.title?.toLowerCase().includes(searchColumn?.toLowerCase())) : [];
        },

        renderItem: (item, setIsSelected, selectedIds, columnIdPrefix) => {
            return <KanbanCard
                key={item.id}
                item={item}
                setIsSelected={setIsSelected}
                selectedIds={selectedIds}
                showContent={(localAvatar, perm) => (
                    <>
                        <p className="">{perm.name}</p>
                    </>
                )}
                columnIdPrefix={columnIdPrefix}
            />
        },

        renderItemFilter: (allItems, handleNewPerm, setIsSelected, selectedIds, columnIdPrefix, currentPage, elementsPerPage) => {
            return allItems
                .slice((currentPage - 1) * elementsPerPage, currentPage * elementsPerPage)
                .map((perm) => {
                    return (
                        <KanbanCard
                            key={perm.id}
                            item={perm}
                            setIsSelected={setIsSelected}
                            selectedIds={selectedIds}
                            showContent={(localAvatar, p) => (
                                <>
                                    <p className="">{p.name}</p>
                                </>
                            )}
                            clickAction={() => handleNewPerm(perm)}
                            columnIdPrefix={columnIdPrefix}
                        />
                    );
                })
        },

        filterCards: (permissions, searchTerm, role) => {
            return permissions.filter(
                (perm) => perm.role === role.role_id && perm.name?.toLowerCase().includes(searchTerm?.toLowerCase())
            );
        },

        filterAllCards: (allItems, items, column) => {
            return allItems
                .filter((u) => items.find((fu) => fu.permission_id === u.id && fu.role === column.role_id) === undefined)
        },

        getAllExternalItems: async (config, selectedEmployeeAvatar) => {
            const localPerms = await dispatch(getAllPermissions());
            const allPerms = localPerms?.payload || [];
            return allPerms;
        },

        onAddItem: async (config, permissions, perm, role) => {
            let localPermissions = [...permissions];
            await dispatch(
                addRolePermission({
                    params: [{ permission_id: perm.id, role_id: role.pos }],
                })
            );
            localPermissions.push({
                id: localPermissions.length !== 0 ? localPermissions.at(-1).id + 1 : 0,
                name: perm.name,
                role: role.role_id,
            });
            return localPermissions;
        },

        onDeleteItems: async (config, selectedIds, permissions, role) => {
            const apiCalls = selectedIds.map(async (id) => {
                const perm = permissions.find(u => u.id === id);
                if (perm) {
                    await dispatch(removeRolePermission({
                        params: [{ permission_id: perm.permission_id, role_id: role.pos }]
                    }));
                }
            });

            // Wait for all API calls to complete
            await Promise.all(apiCalls);

            // Create a new array by filtering out the deleted items
            const filteredPermissions = permissions.filter(u => !selectedIds.includes(u.id));

            return filteredPermissions;
        },

        dragOverlayShow: (activeId, permissions, setIsSelected, columnIdPrefix) => {
            if (!permissions) return <div>Empty</div>;
            return (
                <KanbanCard
                    key={activeId}
                    item={permissions.find((perm) => perm.id === activeId)}
                    setIsSelected={setIsSelected}
                    selectedIds={[]}
                    showContent={(localAvatar, perm) => (
                        <>
                            <p className="">{perm.name.toUpperCase()}</p>
                        </>
                    )}
                    columnIdPrefix={columnIdPrefix}
                />
            );
        },

        onDeleteColumn: async (role) => {
            const roleId = role.role_id;
            const success = await dispatch(deleteRole({params:[{ role_id: roleId }]})).unwrap();
            if (!success) {
                console.error("Failed to delete role with id:", roleId);
                return false;
            }
            return true;
        }
    };

    return (
        <>
            <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Kanban
                key={"gestion-roles-kanban"}
                config={gestionRolesConfig}
                sensors={sensors}
                searchColumn={searchTerm}
                selectedEmployeeAvatar={selectedEmployeeAvatar}
                columnIdPrefix="gestion-roles"
                elementsPerPage={elementsPerPage}
            />
        </>
    );
};
